import PptxGenJS from 'pptxgenjs';
import imageService from './imageService';

// Enhanced HTML section parser with formatting support
function parseHtmlSectionsEnhanced(html) {
  const sections = [];
  
  if (!html) return sections;
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Process each top-level element
  const elements = Array.from(doc.body.children);
  
  elements.forEach(element => {
    const section = parseElementToSection(element);
    if (section) {
      sections.push(section);
    }
  });
  
  return sections;
}

function parseElementToSection(element) {
  const tagName = element.tagName.toLowerCase();
  
  switch (tagName) {
    case 'h1':
    case 'h2':
    case 'h3':
      return {
        type: tagName,
        content: extractTextWithFormatting(element),
        rawText: element.textContent.trim(),
        style: extractElementStyle(element)
      };
      
    case 'p':
      const textContent = extractTextWithFormatting(element);
      const rawText = element.textContent.trim();
      if (rawText) {
        return {
          type: 'p',
          content: textContent,
          rawText: rawText,
          style: extractElementStyle(element)
        };
      }
      break;
      
    case 'ul':
    case 'ol':
      const items = Array.from(element.querySelectorAll('li')).map(li => ({
        text: li.textContent.trim(),
        style: extractElementStyle(li)
      }));
      return {
        type: tagName,
        items: items,
        style: extractElementStyle(element)
      };
      
    case 'table':
      return parseTableElement(element);
      
    case 'img':
      return parseImageElement(element);
      
    case 'blockquote':
      return {
        type: 'blockquote',
        content: extractTextWithFormatting(element),
        rawText: element.textContent.trim(),
        style: extractElementStyle(element)
      };
      
    default:
      // For other elements, try to extract text content
      const text = element.textContent.trim();
      if (text) {
        return {
          type: 'text',
          content: extractTextWithFormatting(element),
          rawText: text,
          style: extractElementStyle(element)
        };
      }
  }
  
  return null;
}

function extractTextWithFormatting(element) {
  // Extract text with inline formatting preserved
  const parts = [];
  
  function processNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.trim()) {
        parts.push({
          text: text,
          formatting: getFormattingFromAncestors(node)
        });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      Array.from(node.childNodes).forEach(processNode);
    }
  }
  
  processNode(element);
  return parts;
}

function getFormattingFromAncestors(textNode) {
  const formatting = {};
  let current = textNode.parentElement;
  
  while (current && current.tagName) {
    const tagName = current.tagName.toLowerCase();
    const style = current.style || {};
    
    // Tag-based formatting
    switch (tagName) {
      case 'strong':
      case 'b':
        formatting.bold = true;
        break;
      case 'em':
      case 'i':
        formatting.italic = true;
        break;
      case 'u':
        formatting.underline = true;
        break;
      case 'code':
        formatting.fontFace = 'Courier New';
        break;
      default:
        break;
    }
    
    // Style-based formatting
    if (style.fontWeight === 'bold' || parseInt(style.fontWeight) >= 600) {
      formatting.bold = true;
    }
    if (style.fontStyle === 'italic') {
      formatting.italic = true;
    }
    if (style.textDecoration?.includes('underline')) {
      formatting.underline = true;
    }
    if (style.color) {
      formatting.color = convertColorToPptx(style.color);
    }
    if (style.fontSize) {
      formatting.fontSize = convertFontSizeToPptx(style.fontSize);
    }
    if (style.fontFamily) {
      formatting.fontFace = style.fontFamily.replace(/['"]/g, '').split(',')[0];
    }
    
    current = current.parentElement;
  }
  
  return formatting;
}

function extractElementStyle(element) {
  const style = {};
  const computedStyle = element.style || {};
  
  // Text alignment
  if (computedStyle.textAlign) {
    switch (computedStyle.textAlign) {
      case 'center':
        style.align = 'center';
        break;
      case 'right':
        style.align = 'right';
        break;
      case 'justify':
        style.align = 'justify';
        break;
      default:
        style.align = 'left';
    }
  }
  
  // Background color
  if (computedStyle.backgroundColor && computedStyle.backgroundColor !== 'transparent') {
    style.fill = convertColorToPptx(computedStyle.backgroundColor);
  }
  
  // Margins and padding
  if (computedStyle.marginTop) {
    style.marginTop = parseFloat(computedStyle.marginTop);
  }
  if (computedStyle.marginBottom) {
    style.marginBottom = parseFloat(computedStyle.marginBottom);
  }
  
  return style;
}

function parseTableElement(element) {
  const rows = Array.from(element.querySelectorAll('tr'));
  const tableData = rows.map(row => {
    const cells = Array.from(row.querySelectorAll('td, th'));
    return cells.map(cell => ({
      text: cell.textContent.trim(),
      options: {
        bold: cell.tagName.toLowerCase() === 'th',
        fill: cell.tagName.toLowerCase() === 'th' ? 'f2f2f2' : 'ffffff',
        color: '333333',
        fontSize: 11,
        align: 'left',
        valign: 'top'
      }
    }));
  });
  
  return {
    type: 'table',
    data: tableData,
    style: extractElementStyle(element)
  };
}

function parseImageElement(element) {
  const src = element.src;
  const alt = element.alt || '';
  const width = element.width || element.naturalWidth;
  const height = element.height || element.naturalHeight;
  
  return {
    type: 'image',
    src: src,
    alt: alt,
    width: width,
    height: height,
    style: extractElementStyle(element)
  };
}

function convertColorToPptx(color) {
  // Convert CSS color to PowerPoint color format
  if (color.startsWith('#')) {
    return color.substring(1);
  }
  if (color.startsWith('rgb')) {
    const matches = color.match(/\d+/g);
    if (matches && matches.length >= 3) {
      return matches.slice(0, 3)
        .map(n => parseInt(n).toString(16).padStart(2, '0'))
        .join('');
    }
  }
  // Default colors
  const colorMap = {
    'black': '000000',
    'white': 'ffffff',
    'red': 'ff0000',
    'green': '00ff00',
    'blue': '0000ff',
    'yellow': 'ffff00',
  };
  return colorMap[color.toLowerCase()] || '333333';
}

function convertFontSizeToPptx(fontSize) {
  if (fontSize.endsWith('px')) {
    return Math.round(parseFloat(fontSize) * 0.75); // Convert px to pt
  }
  if (fontSize.endsWith('pt')) {
    return parseFloat(fontSize);
  }
  if (fontSize.endsWith('em')) {
    return Math.round(parseFloat(fontSize) * 12); // Assume 12pt base
  }
  return 12; // Default
}

class EnhancedPptxService {
  /**
   * Enhanced slide export with formatting and images
   */
  async exportSlidesToPptx(slides, fileName, options = {}) {
    const pptx = new PptxGenJS();
    pptx.layout = 'LAYOUT_16x9';
    
    // Enhanced theme colors
    const theme = {
      titleColor: options.titleColor || '1a1a1a',
      bodyColor: options.bodyColor || '333333',
      accentColor: options.accentColor || '1976d2',
      backgroundColor: options.backgroundColor || 'ffffff'
    };
    
    const safeSlides = Array.isArray(slides) ? slides : [];
    
    // Process images before creating slides
    const processedImages = await this.preprocessImages(safeSlides);
    
    for (let i = 0; i < safeSlides.length; i++) {
      const slideDef = safeSlides[i];
      await this.createEnhancedSlide(pptx, slideDef, i, theme, processedImages);
    }
    
    // Save the presentation
    await pptx.writeFile({ fileName: fileName });
    return true;
  }

  /**
   * Enhanced HTML to PowerPoint conversion
   */
  async exportHtmlToPptx(htmlContent, fileName, options = {}) {
    const sections = parseHtmlSectionsEnhanced(htmlContent);
    const slides = this.convertSectionsToSlides(sections, options);
    
    return await this.exportSlidesToPptx(slides, fileName, options);
  }

  /**
   * Preprocess images for PowerPoint
   */
  async preprocessImages(slides) {
    const imageMap = new Map();
    
    for (const slide of slides) {
      if (slide.content) {
        const images = this.extractImagesFromContent(slide.content);
        
        for (const img of images) {
          if (img.src.startsWith('data:')) {
            const imageId = this.getImageIdFromSrc(img.src);
            const imageData = imageService.getImage(imageId);
            
            if (imageData) {
              // Convert to format suitable for PowerPoint
              const pptxImageData = {
                data: imageData.dataUrl,
                width: img.width || imageData.width,
                height: img.height || imageData.height,
                alt: img.alt || imageData.name
              };
              
              imageMap.set(img.src, pptxImageData);
            }
          }
        }
      }
    }
    
    return imageMap;
  }

  /**
   * Create enhanced slide with formatting
   */
  async createEnhancedSlide(pptx, slideDef, index, theme, processedImages) {
    const slide = pptx.addSlide();
    
    // Set slide background
    if (theme.backgroundColor && theme.backgroundColor !== 'ffffff') {
      slide.background = { fill: theme.backgroundColor };
    }
    
    // Parse slide content
    let sections = [];
    if (typeof slideDef.content === 'string') {
      sections = parseHtmlSectionsEnhanced(slideDef.content);
    } else if (Array.isArray(slideDef.content)) {
      sections = slideDef.content;
    }
    
    // Determine slide title
    const titleSection = sections.find(s => ['h1', 'h2', 'h3'].includes(s.type));
    const title = slideDef.title || titleSection?.rawText || `Slide ${index + 1}`;
    
    // Add title
    slide.addText(title, {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 1,
      fontSize: 28,
      bold: true,
      color: theme.titleColor,
      align: 'center'
    });
    
    // Add content sections
    let currentY = 1.5;
    const contentWidth = 9;
    const slideHeight = 6.5; // Available height for content
    
    for (const section of sections) {
      if (section === titleSection) continue; // Skip title section
      
      const sectionHeight = await this.addSectionToSlide(
        slide, 
        section, 
        0.5, 
        currentY, 
        contentWidth, 
        theme, 
        processedImages
      );
      
      currentY += sectionHeight + 0.2; // Add spacing between sections
      
      // Check if we need a new slide
      if (currentY > slideHeight) {
        break; // Stop adding content to this slide
      }
    }
  }

  /**
   * Add section to slide with proper formatting
   */
  async addSectionToSlide(slide, section, x, y, width, theme, processedImages) {
    switch (section.type) {
      case 'h1':
      case 'h2':
      case 'h3':
        return this.addHeadingToSlide(slide, section, x, y, width, theme);
        
      case 'p':
      case 'text':
        return this.addTextToSlide(slide, section, x, y, width, theme);
        
      case 'ul':
      case 'ol':
        return this.addListToSlide(slide, section, x, y, width, theme);
        
      case 'table':
        return this.addTableToSlide(slide, section, x, y, width, theme);
        
      case 'image':
        return await this.addImageToSlide(slide, section, x, y, width, processedImages);
        
      case 'blockquote':
        return this.addBlockquoteToSlide(slide, section, x, y, width, theme);
        
      default:
        return 0;
    }
  }

  /**
   * Add formatted heading to slide
   */
  addHeadingToSlide(slide, section, x, y, width, theme) {
    const fontSize = section.type === 'h1' ? 20 : section.type === 'h2' ? 18 : 16;
    
    slide.addText(section.rawText, {
      x: x,
      y: y,
      w: width,
      h: 0.6,
      fontSize: fontSize,
      bold: true,
      color: theme.accentColor,
      align: section.style?.align || 'left'
    });
    
    return 0.8;
  }

  /**
   * Add formatted text to slide
   */
  addTextToSlide(slide, section, x, y, width, theme) {
    if (Array.isArray(section.content)) {
      // Rich text with formatting
      const textOptions = {
        x: x,
        y: y,
        w: width,
        h: 'auto',
        fontSize: 14,
        color: theme.bodyColor,
        align: section.style?.align || 'left',
        breakLine: true
      };
      
      // Convert formatted content to PowerPoint text runs
      const richText = section.content.map(part => ({
        text: part.text,
        options: {
          ...part.formatting,
          fontSize: part.formatting.fontSize || 14,
          color: part.formatting.color || theme.bodyColor
        }
      }));
      
      slide.addText(richText, textOptions);
    } else {
      // Plain text
      slide.addText(section.rawText, {
        x: x,
        y: y,
        w: width,
        h: 'auto',
        fontSize: 14,
        color: theme.bodyColor,
        align: section.style?.align || 'left'
      });
    }
    
    // Estimate height based on text length
    const lines = Math.ceil(section.rawText.length / 80);
    return Math.max(0.5, lines * 0.25);
  }

  /**
   * Add formatted list to slide
   */
  addListToSlide(slide, section, x, y, width, theme) {
    const isOrdered = section.type === 'ol';
    const listText = section.items.map((item, index) => {
      const bullet = isOrdered ? `${index + 1}.` : '•';
      return `${bullet} ${item.text}`;
    }).join('\n');
    
    slide.addText(listText, {
      x: x + 0.2,
      y: y,
      w: width - 0.2,
      h: 'auto',
      fontSize: 14,
      color: theme.bodyColor,
      align: 'left',
      bullet: !isOrdered
    });
    
    return Math.max(0.5, section.items.length * 0.3);
  }

  /**
   * Add table to slide
   */
  addTableToSlide(slide, section, x, y, width, theme) {
    const tableOptions = {
      x: x,
      y: y,
      w: width,
      fontSize: 11,
      border: { pt: 1, color: 'cccccc' },
      fill: 'ffffff'
    };
    
    slide.addTable(section.data, tableOptions);
    
    // Estimate table height
    const rowHeight = 0.4;
    return section.data.length * rowHeight + 0.2;
  }

  /**
   * Add image to slide
   */
  async addImageToSlide(slide, section, x, y, width, processedImages) {
    const imageData = processedImages.get(section.src);
    
    if (!imageData) {
      console.warn('Image not found for slide:', section.src);
      return 0.5;
    }
    
    // Calculate dimensions to fit within slide
    const maxWidth = Math.min(width * 0.8, 6);
    const maxHeight = 3;
    
    let imgWidth = imageData.width / 96; // Convert pixels to inches (96 DPI)
    let imgHeight = imageData.height / 96;
    
    // Scale down if necessary
    if (imgWidth > maxWidth) {
      imgHeight = (imgHeight * maxWidth) / imgWidth;
      imgWidth = maxWidth;
    }
    
    if (imgHeight > maxHeight) {
      imgWidth = (imgWidth * maxHeight) / imgHeight;
      imgHeight = maxHeight;
    }
    
    // Center the image horizontally
    const imgX = x + (width - imgWidth) / 2;
    
    slide.addImage({
      data: imageData.data,
      x: imgX,
      y: y,
      w: imgWidth,
      h: imgHeight
    });
    
    return imgHeight + 0.3;
  }

  /**
   * Add blockquote to slide
   */
  addBlockquoteToSlide(slide, section, x, y, width, theme) {
    // Add a colored background box
    slide.addShape(PptxGenJS.ShapeType.rect, {
      x: x,
      y: y,
      w: width,
      h: 'auto',
      fill: { color: 'f8f9fa' },
      line: { color: theme.accentColor, width: 3, dashType: 'solid' }
    });
    
    slide.addText(section.rawText, {
      x: x + 0.2,
      y: y + 0.1,
      w: width - 0.4,
      h: 'auto',
      fontSize: 14,
      italic: true,
      color: '555555',
      align: 'left'
    });
    
    // Estimate height
    const lines = Math.ceil(section.rawText.length / 70);
    return Math.max(0.8, lines * 0.25 + 0.3);
  }

  /**
   * Convert HTML sections to slide definitions
   */
  convertSectionsToSlides(sections, options = {}) {
    const slides = [];
    let currentSlide = {
      title: '',
      content: []
    };
    
    const maxSectionsPerSlide = options.maxSectionsPerSlide || 4;
    let sectionsInCurrentSlide = 0;
    
    for (const section of sections) {
      // Create new slide for major headings or when current slide is full
      if (section.type === 'h1' || 
          (section.type === 'h2' && sectionsInCurrentSlide > 0) ||
          sectionsInCurrentSlide >= maxSectionsPerSlide) {
        
        if (currentSlide.content.length > 0) {
          slides.push({ ...currentSlide });
        }
        
        currentSlide = {
          title: section.type.startsWith('h') ? section.rawText : '',
          content: [section]
        };
        sectionsInCurrentSlide = 1;
      } else {
        currentSlide.content.push(section);
        sectionsInCurrentSlide++;
      }
    }
    
    // Add the last slide
    if (currentSlide.content.length > 0) {
      slides.push(currentSlide);
    }
    
    return slides;
  }

  /**
   * Extract images from slide content
   */
  extractImagesFromContent(content) {
    if (typeof content === 'string') {
      return imageService.extractImagesFromHtml(content);
    }
    
    if (Array.isArray(content)) {
      const images = [];
      for (const section of content) {
        if (section.type === 'image') {
          images.push(section);
        }
      }
      return images;
    }
    
    return [];
  }

  /**
   * Get image ID from data URL
   */
  getImageIdFromSrc(src) {
    // Try to extract image ID from data URL or HTML attributes
    if (src.includes('data-image-id')) {
      const match = src.match(/data-image-id="([^"]+)"/);
      return match ? match[1] : null;
    }
    
    // Fallback: look up by data URL
    const allImages = imageService.getAllImages();
    const matchingImage = allImages.find(img => img.dataUrl === src);
    return matchingImage ? matchingImage.id : null;
  }
}

const enhancedPptxService = new EnhancedPptxService();
export default enhancedPptxService;
